/**
 * Contact Page Pro
 *
 * Features:
 * - Entrance animation
 * - WhatsApp form submission
 * - Client-side invalid field hint
 * - Input focus state
 * - Sending button state
 * - Shopify Theme Editor section reload support
 *
 * IMPORTANT:
 * Everything is scoped to .contact-pro.
 * This script does NOT modify the Shopify header/navigation.
 */

(function () {
  'use strict';

  function initEntranceAnimation(scope) {
    var sections = scope.querySelectorAll(
      '.contact-pro.cs-animate'
    );

    if (!sections.length) {
      return;
    }

    if (!('IntersectionObserver' in window)) {
      sections.forEach(function (section) {
        section.classList.add('cs-in-view');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) {
            return;
          }

          var section = entry.target;

          var duration =
            section.getAttribute('data-anim-duration') || '600';

          var delay =
            section.getAttribute('data-anim-delay') || '0';

          section.style.setProperty(
            '--cs-anim-duration',
            duration + 'ms'
          );

          section.style.setProperty(
            '--cs-anim-delay',
            delay + 'ms'
          );

          section.classList.add('cs-in-view');
          obs.unobserve(section);
        });
      },
      {
        threshold: 0.15,
        rootMargin: '0px 0px -40px 0px'
      }
    );

    sections.forEach(function (section) {
      observer.observe(section);
    });
  }

  function showMessage(section, type) {
    var selector =
      type === 'success'
        ? '[data-cs-success]'
        : '[data-cs-error]';

    var message = section.querySelector(selector);

    if (!message) {
      return;
    }

    message.hidden = false;

    message.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  }

  function initFormFeedback(scope) {
    var sections = scope.querySelectorAll('.contact-pro');

    if (!sections.length) {
      return;
    }

    var params = new URLSearchParams(
      window.location.search
    );

    var posted =
      params.get('contact_posted') === 'true' ||
      window.__csFormPostedSuccessfully === true;

    sections.forEach(function (section) {
      var errorMessage =
        section.querySelector('[data-cs-error]');

      var hasServerError =
        errorMessage &&
        !errorMessage.hidden;

      if (posted && !hasServerError) {
        showMessage(section, 'success');
      }
    });
  }

  function initClientValidation(scope) {
    var forms = scope.querySelectorAll(
      '.contact-pro .cs-form'
    );

    forms.forEach(function (form) {
      if (form.dataset.csValidationInitialized === 'true') {
        return;
      }

      form.dataset.csValidationInitialized = 'true';

      form.addEventListener('submit', function () {
        var invalid = form.querySelector(':invalid');

        if (!invalid) {
          return;
        }

        invalid.classList.add('cs-input-invalid');

        invalid.addEventListener(
          'input',
          function () {
            invalid.classList.remove('cs-input-invalid');
          },
          { once: true }
        );
      });
    });
  }

  function initInputFocus(scope) {
    var inputs = scope.querySelectorAll(
      '.contact-pro input, .contact-pro textarea'
    );

    inputs.forEach(function (input) {
      if (input.dataset.csFocusInitialized === 'true') {
        return;
      }

      input.dataset.csFocusInitialized = 'true';

      input.addEventListener('focus', function () {
        var group = this.closest('.form-group');

        if (group) {
          group.classList.add('active');
        }
      });

      input.addEventListener('blur', function () {
        var group = this.closest('.form-group');

        if (group) {
          group.classList.remove('active');
        }
      });
    });
  }

  function getFieldValue(form, selector) {
    var field = form.querySelector(selector);
    return field ? field.value.trim() : '';
  }

  function initFormSubmit(scope) {
    var forms = scope.querySelectorAll(
      '.contact-pro .cs-form'
    );

    forms.forEach(function (form) {
      if (form.dataset.csSubmitInitialized === 'true') {
        return;
      }

      form.dataset.csSubmitInitialized = 'true';

      form.addEventListener('submit', function (event) {
        /*
         * STOP Shopify's native contact-form submission.
         * The filled form data will be sent to WhatsApp instead.
         */
        event.preventDefault();

        if (!form.checkValidity()) {
          form.reportValidity();
          return;
        }

        var section = form.closest('.contact-pro');
        var button = form.querySelector('.contact-btn');

        if (!section || !button) {
          return;
        }

        var whatsappNumber =
          section.getAttribute('data-whatsapp-number') ||
          button.getAttribute('data-whatsapp-number') ||
          '';

        whatsappNumber = whatsappNumber.replace(/\D/g, '');

        if (!whatsappNumber) {
          alert(
            'Please add a WhatsApp number in the Contact page section settings.'
          );
          return;
        }

        var name = getFieldValue(
          form,
          'input[name="contact[name]"]'
        );

        var email = getFieldValue(
          form,
          'input[name="contact[email]"]'
        );

        var phone = getFieldValue(
          form,
          'input[name="contact[phone]"]'
        );

        var message = getFieldValue(
          form,
          'textarea[name="contact[body]"]'
        );

        var text =
          'Hello TRU PAHADI! 👋\n\n' +
          '*Contact Enquiry*\n' +
          '━━━━━━━━━━━━━━━━━━\n\n' +
          '*Name:* ' + name + '\n' +
          '*Email:* ' + email + '\n' +
          '*Phone:* ' + (phone || 'Not provided') + '\n' +
          '*Message:*\n' + message + '\n\n' +
          '━━━━━━━━━━━━━━━━━━\n' +
          'Please get in touch with me regarding my enquiry.';

        var whatsappUrl =
          'https://wa.me/' +
          whatsappNumber +
          '?text=' +
          encodeURIComponent(text);

        var originalText =
          button.textContent.trim();

        button.textContent = 'Opening WhatsApp...';
        button.disabled = true;

        var popup = window.open(
          whatsappUrl,
          '_blank',
          'noopener,noreferrer'
        );

        /*
         * If the browser blocks the new tab, use the same tab.
         */
        if (!popup) {
          window.location.href = whatsappUrl;
          return;
        }

        setTimeout(function () {
          button.textContent = originalText;
          button.disabled = false;
        }, 1500);
      });
    });
  }

  function init(scope) {
    scope = scope || document;

    initEntranceAnimation(scope);
    initFormFeedback(scope);
    initClientValidation(scope);
    initInputFocus(scope);
    initFormSubmit(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        init(document);
      }
    );
  } else {
    init(document);
  }

  document.addEventListener(
    'shopify:section:load',
    function (event) {
      if (
        event.target &&
        event.target.querySelector('.contact-pro')
      ) {
        init(event.target);
      }
    }
  );
})();
